import { FC } from 'react';
import { formatCurrencyCart } from '../../utils/formatters';
import { translate } from '../../i18n';
import { installmentOptionStyles } from './styles';

export interface InstallmentPlanBadge {
  text: string;
  variant: 'success' | 'warning';
}

export interface InstallmentOptionProps {
  months: number;
  monthlyAmount: number;
  totalAmount: number;
  interestRate?: string;
  badge?: InstallmentPlanBadge;
  isSelected?: boolean;
  onPress?: () => void;
  testID?: string;
  accessibilityLabel?: string;
  /** Muestra el radio button a la izquierda. Default true (usado por InstallmentSelector). */
  showRadio?: boolean;
  /** Reserva el alto de la segunda fila aunque no tenga contenido, para que todas las filas midan igual. */
  reserveSecondRow?: boolean;
}

/**
 * InstallmentOption — Opción de cuota dentro del selector de cuotas
 *
 * Cambios:
 * ✅ Estilos centralizados en styles.ts
 * ✅ Radio button customizado sin Tailwind
 * ✅ Badge con soporte para variantes success/warning
 * ✅ Cero hardcodes
 */
export const InstallmentOption: FC<InstallmentOptionProps> = ({
  months,
  monthlyAmount,
  totalAmount,
  interestRate,
  badge,
  isSelected = false,
  onPress,
  testID = 'installment-option',
  accessibilityLabel,
  showRadio = true,
  reserveSecondRow = false,
}) => (
  <label
    onClick={onPress}
    role="button"
    data-testid={testID}
    aria-label={accessibilityLabel}
    style={installmentOptionStyles.container}
  >
    {/* Radio button - styled custom */}
    {showRadio && (
      <div
        style={{
          ...installmentOptionStyles.radioButton,
          ...(isSelected
            ? installmentOptionStyles.radioButtonSelected
            : installmentOptionStyles.radioButtonUnselected),
        }}
        data-testid={`${testID}-radio`}
      >
        {isSelected && <div style={installmentOptionStyles.radioDot} />}
        <input
          type="radio"
          checked={isSelected}
          onChange={() => onPress?.()}
          style={installmentOptionStyles.radioInput}
          aria-hidden="true"
        />
      </div>
    )}

    {/* Content container */}
    <div style={installmentOptionStyles.contentContainer}>
      {/* Main row: Installments + Badge + Total */}
      <div style={installmentOptionStyles.mainRow}>
        <span style={installmentOptionStyles.installmentLabel}>
          {months > 1
            ? translate('paymentsUI.installment.pluralWithSuffix', { months })
            : translate('paymentsUI.installment.singular', { months })}
        </span>

        {badge && badge.variant === 'warning' && (
          <span style={installmentOptionStyles.badge} data-testid={`${testID}-badge`}>
            {badge.text}
          </span>
        )}

        <div style={installmentOptionStyles.flexSpacer} />

        <div style={installmentOptionStyles.totalPrice}>{formatCurrencyCart(totalAmount)}</div>
      </div>

      {/* Second row: Success badge OR Interest % + Monthly details */}
      {reserveSecondRow || (badge && badge.variant === 'success') || interestRate || months > 1 ? (
        <div style={installmentOptionStyles.secondRow}>
          {badge && badge.variant === 'success' ? (
            <span style={installmentOptionStyles.badge} data-testid={`${testID}-badge`}>
              {badge.text}
            </span>
          ) : interestRate ? (
            <div style={installmentOptionStyles.interestRate}>{interestRate}</div>
          ) : null}

          <div style={installmentOptionStyles.flexSpacer} />

          {months > 1 && (
            <div style={installmentOptionStyles.monthlyDetails}>
              ({months} x {formatCurrencyCart(monthlyAmount)})
            </div>
          )}
        </div>
      ) : null}
    </div>
  </label>
);
